import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import { dispatchNativeEvent } from '../../global/events';
import styles from './ui-view.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIView
 * @element ui-view
 * @implements {Activatable}
 * @classdesc Represents a class for <code>ui-view</code> element.
 * Element for changing views, can be only child of ui-views container.
 * @fires event:change
 * @property {boolean} [active] {@attr active} If true, current view will be active,
 * otherwise inactive.
 * @property {string} [label] {@attr label} The caption of current view,
 * it is used for tabs, wizard etc.
 * @slot
 * @example <ui-view label="This is a header"></ui-view>
 */
@Component({ tag: 'ui-view', styles })
export class UIView extends UIElement {
    @Attribute(Boolean) active?: boolean;
    @Attribute(String) label?: string;

    static get observedAttributes(): ObservedAttributes {
        return ['active'];
    }

    /**
     * Changes current view to active (visible) state.
     * @fires event:change
     * @returns {UIView}
     */
    activate(): UIView {
        if (this.active) {
            return this;
        }
        this.active = true;
        dispatchNativeEvent(this, 'change', false);
        return this;
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'active':
                this.active ? this.show() : this.hide();
                break;
        }
    }
}
